import React, { useState } from "react";
import {
  Button,
  Box,
  TextField,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Card,
  Container,
  Link,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import "../../Styles/Login.css";
import { useFormik } from "formik";
import { loginschema } from "../../Component/Schemas/loginSchema";
import { GetUserRoles, UserSignIn } from "../../Redux/Actions/authAction";
import { useDispatch } from "react-redux";
import { GetUsers } from "../../Redux/Actions/userStatusService";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import TwoFactorCode from "./TwoFactorCode";
import LightLogo from "../../Images/P21_Light_logo.png";

import rightSidePic from "../../Images/right_side_pic.png";
import { useUser } from "../../Context/UserContext";
import { usePermissions } from "../../Context/PermissionContext";
import { clearAuthDetails } from "../../Redux/Reducers/authReducer";

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    subtitle1: {
      color: '#666',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8fafc',
          borderRadius: 8,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '10px 0',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          maxWidth: '750px',
          width: '100%',
          minHeight: '600px',
          padding: '2.5rem',
          margin: '2rem auto',
          '@media (max-width: 600px)': {
            padding: '1.5rem',
            margin: '1rem',
            minHeight: 'auto',
          },
        },
      },
    },
  },
});

export default function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateUserInfo } = useUser();
  const { updatePermissions } = usePermissions();
  var initialValues = { email: "", password: "" };
  const [showPassword, setShowPassword] = React.useState(false);
  const [showTwoFactorCode, setShowTwoFactorCode] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: loginschema,
      onSubmit: async (values) => {
        const { email, password } = values;
        const portal = "MANAGER";
        
        // Store email and password for potential 2FA use
        setUserEmail(email);
        setUserPassword(password);

        try {
          const response = await dispatch(
            UserSignIn({ email, password, portal })
          );

          // Check if 2FA is required
          if (response?.payload?.responseData?.statusCode === 201 && 
              response?.payload?.responseData?.data?.isTwoFactorRequired) {
            // Show 2FA component
            setShowTwoFactorCode(true);
          } else if (response?.payload?.responseData?.statusCode === 201) {
            // Store user info in context
            updateUserInfo(response.payload.responseData);
            
            // Check if response contains userRoles (multiple roles case)
            // New structure: data.user.userRoles or old structure: data.userRoles
            const userRoles = response?.payload?.responseData?.data?.user?.userRoles || response?.payload?.responseData?.data?.userRoles;
            if (userRoles) {
              
              // Check if user has MANAGER role among other roles
              const hasManagerRole = userRoles.some(role => role.role === "MANAGER");
              
              if (hasManagerRole) {
                // If user has MANAGER role along with other roles, route to select account
                navigate("/");
              } else {
                // If all roles are SUB_MANAGER, check their parents array
                const subManagerRoles = userRoles.filter(role => role.role === "SUB_MANAGER");
                
        
                if (subManagerRoles.length > 0) {
                  const allParentsEmpty = subManagerRoles.every(role => Array.isArray(role.parents) && role.parents.length === 0);
                 
                  if (allParentsEmpty) {
                   
                    // No parents assigned - redirect to support contact page
                    // Clear any tokens that might have been stored by Redux
                    localStorage.removeItem("token");
                    localStorage.removeItem("refreshToken");
                    sessionStorage.clear();
                    dispatch(clearAuthDetails()); // Clear Redux state
                    navigate("/supportcontact");
                    return; // Add return to prevent further execution
                  } else {
                    // If all have non-empty parents and all parents are PENDING, go to requests
                    const allHaveParents = subManagerRoles.every(role => Array.isArray(role.parents) && role.parents.length > 0);
                    const allPending = allHaveParents && subManagerRoles.every(role =>
                      role.parents.every(parent => parent.status === 'PENDING')
                    );
                   
                    if (allPending) {
                      navigate("/requests");
                      return; // Add return to prevent further execution
                    } else {
                      navigate("/");
                      return; // Add return to prevent further execution
                    }
                  }
                }
              }
            } else {
              // Single role case - direct tokens in response
              // Check for both new structure (data.user.userRole) and old structure (data.userRole)
              const userRole = response?.payload?.responseData?.data?.user?.userRole || response?.payload?.responseData?.data?.userRole;
              
              if (userRole) {
                // Check if SUB_MANAGER has no parents before proceeding
                if (userRole.role === "SUB_MANAGER" && 
                    (!userRole.parents || userRole.parents.length === 0)) {
               
                  localStorage.removeItem("token");
                  localStorage.removeItem("refreshToken");
                  sessionStorage.clear();
                  dispatch(clearAuthDetails()); // Clear Redux state
                  
                  navigate("/supportcontact");
                  return; // Add return to prevent further execution
                }
                
                // Store role information
                localStorage.setItem("type", userRole.role);
                
                // Extract and store permissions for SUB_MANAGER role only
                if (userRole.role === "SUB_MANAGER" && 
                    userRole.parents?.[0]?.allowedPermissions?.[0]?.permissionGroup?.apiNames) {
                  const permissions = userRole.parents[0].allowedPermissions[0].permissionGroup.apiNames;
                  // Store permissions in context
                  updatePermissions(permissions, userRole.role);
                  
                  // Store parent's entity info for SUB_MANAGER
                  const parentUserRole = userRole.parents?.[0]?.parentUserRole;
                  if (parentUserRole?.entityInfo) {
                    localStorage.setItem("parentEntityName", parentUserRole.entityInfo.name);
                    localStorage.setItem("parentEntityType", parentUserRole.entityInfo.entityType);
                  }
                }
                
                // Continue with normal login flow
                const rolesResponse = await dispatch(GetUserRoles());

                if (rolesResponse?.payload?.statusCode === 200) {
                  dispatch(GetUsers("children"));
                }
              }
            }
          } else {
            // Handle login failure
            console.error("Login failed:", response);
            // You can add error handling here like showing an alert
          }
        } catch (error) {
          console.error("Error during sign-in or role retrieval:", error);
        }
      },
    });

  // If 2FA is required, show the TwoFactorCode component
  if (showTwoFactorCode) {
    return <TwoFactorCode email={userEmail} password={userPassword} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className="login-container">
        {/* Left side - Login form */}
        <Box className="login-form-container">
          <Container 
            maxWidth={false}
            className="login-wrapper"
            // sx={{
            //   maxWidth: '650px',
            //   width: '100%',
            //   height: '100%',
            //   display: 'flex',
            //   alignItems: 'center',
            //   justifyContent: 'center',
            //   py: 4,
             
            // }}
          >
            <Card className="login-card">
              <Box className="login-card-content">
              <Box sx>
                <Typography variant="h5" component="div" className="logo-wrapper" >
                  <Box component="span" className="logo-icon">
                    <img 
                      src={LightLogo} 
                      alt="Company Logo" 
                      style={{ 
                        height: '45px', 
                        width: 'auto',
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  <Box 
                    component="span" 
                    sx={{ 
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      border: '1px solid #1976d2'
                    }}
                  >
                    Manager Panel
                  </Box>
                </Typography>
              </Box>

              <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              <Box>
                <Typography 
                  variant="h4" 
                  // gutterBottom 
                  className="welcome-text"
                  // sx={{ mb: 1, fontSize: '1.75rem', fontWeight: 600 }}
                >
                  Welcome Back, Manager
                </Typography>
                
                <Typography 
                  variant="subtitle1" 
                  className="welcome-subtitle"
                  // sx={{ mb: 4, color: '#666', fontSize: '1rem' }}
                >
                  Please login to your account to continue
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Box className="form-container">
                  <Box className="form-field" >
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Enter your Email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      variant="outlined"
                      autoComplete="off"
                      
                      InputProps={{
                        className: "input-field"
                      }}
                      InputLabelProps={{
                        sx: {
                          fontWeight: 500,
                          color: '#666',
                          '&.Mui-focused': {
                            color: '#1976d2',
                          },
                        }
                      }}
                    />
                  </Box>

                  <Box className="form-field password-field" >
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      label="Enter your Password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      variant="outlined"
                      autoComplete="off"
                      InputProps={{
                        className: "input-field",
                        endAdornment: (
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                      InputLabelProps={{
                        sx: {
                          fontWeight: 500,
                          color: '#666',
                          '&.Mui-focused': {
                            color: '#1976d2',
                          },
                        }
                      }}
                    />
                  </Box>

                  <Box className="remember-options">
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="remember-checkbox"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" className="remember-text" >
                          Remember me
                        </Typography>
                      }
                    />
                    <Link 
                      component="button"
                      type="button"
                      onClick={() => navigate('/forgotpassword')}
                      // underline="none"
                      // className="forgot-password-link"
                      className="forgot-password-link1"
                    >
                      Forgot your password?
                    </Link>
                  </Box>
                </Box>

                <div className="buttons-container">
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    className="signin-button"
                  >
                    Sign In
                  </Button>

                  {/* <Button
                    fullWidth
                    variant="outlined"
                    className="google-signin-button"
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#333',
                      borderColor: '#e0e0e0',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#d0d0d0'
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18">
                      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.53H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                      <path fill="#FBBC05" d="M4.5 10.49a4.8 4.8 0 0 1 0-3.07V5.35H1.83a8 8 0 0 0 0 7.28l2.67-2.14z"/>
                      <path fill="#EA4335" d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.35L4.5 7.42a4.77 4.77 0 0 1 4.48-2.7z"/>
                    </svg>
                    Sign In with Google
                  </Button> */}
                </div>
              </form>
              </Box>
              </Box>
            </Card>
          </Container>
        </Box>

        {/* Right side - Background Image */}
        <Box className="login-background">
          <img 
            src={rightSidePic} 
            alt="Login Background" 
            className="login-background-image"
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
